/**
 * `<DemoPhoneFrame/>` - the member app inside the demo phone (#2824).
 *
 * Mounted at `/demo-phone` (router `basename`), inside
 * `DemoMemberAuthProvider`. The frame opens on the member app's Calendar
 * (`/me`) as the person the address names. Every other path goes back there.
 * The lead page posts a message on a same-origin channel after Publish roster,
 * a demo change or a replay; the frame then drops what it read and reads
 * again, so the phone follows the lead's page without a new pick.
 */
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "../../lib/AuthContext";
import { subscribeDemoPhone } from "../../lib/demoPhone";

export function DemoPhoneFrame({ children }: { children: ReactNode }) {
  const { loading, accountId } = useAuth();
  const queryClient = useQueryClient();

  useEffect(
    () =>
      subscribeDemoPhone(() => {
        void queryClient.invalidateQueries();
      }),
    [queryClient],
  );

  if (loading) {
    return <OneLine testid="demo-phone-loading">Opening the phone…</OneLine>;
  }
  if (!accountId) {
    return (
      <OneLine testid="demo-phone-problem">
        This phone could not open. Pick the person again.
      </OneLine>
    );
  }
  return (
    <Routes>
      <Route index element={<Navigate to="/me" replace />} />
      {children}
      <Route path="*" element={<Navigate to="/me" replace />} />
    </Routes>
  );
}

function OneLine({ children, testid }: { children: ReactNode; testid: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-6">
      <p className="max-w-xs text-center text-sm text-fg-subtle" data-testid={testid}>
        {children}
      </p>
    </div>
  );
}
